import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  BsDeviceSsd,
  BsPeopleFill,
  BsSendFill,
  BsEnvelopeFill,
  BsCheckCircleFill,
  BsWifi,
  BsWifiOff,
  BsTrophyFill,
  BsWhatsapp,
  BsChatFill,
  BsAndroid2,
  BsBagPlus,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import { notify } from "../components/Notify";
import Sidebar from "./Sidebar";
import Header from "./Header";
import "./Dashboard.css";

const initialStats = {
  totalDevices: 0,
  totalUsers: 0,
  totalSmsSent: 0,
  totalSmsReceived: 0,
  activeDevices: 0,
  inactiveDevices: 0,
  deliveryRate: 0,
};

const CHART_COLORS = {
  sent: "#7c7cff",
  received: "#00b9ff",
  sim1: "#7c7cff",
  sim2: "#8b5cf6",
};

function fmtAgo(ts) {
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [senderOptions, setSenderOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSenders, setSelectedSenders] = useState([]);
  const [services, setServices] = useState({});
  const [counts, setCounts] = useState({});
  const [cm, setCm] = useState({}); // per-channel metrics
  const [activity, setActivity] = useState([]);
  const [analytics, setAnalytics] = useState({
    stats: initialStats,
    simUsage: [],
    deviceLeaders: [],
    trend: [],
  });

  const hasService = (s) => services[s]?.status === "active";
  const anyService = hasService("sms") || hasService("whatsapp") || hasService("imessage");

  // Which services this company has (drives which cards show).
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) { window.location.href = "/login"; return; }
    axios.get(`${API_BASE_URL}/services`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { setServices(res.data.services || {}); setCounts(res.data.counts || {}); })
      .catch(() => {});
  }, []);

  // Recent activity — unified feed across all channels.
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return;
    axios.get(`${API_BASE_URL}/dashboard/activity`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setActivity(r.data.activity || []))
      .catch(() => {});
  }, []);

  // Unified "Sender" filter: SMS devices + WhatsApp numbers + iMessage handles.
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      const [u, dev, wa, im] = await Promise.all([
        axios.get(`${API_BASE_URL}/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/dashboard/devices`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/whatsapp/numbers`, { headers }).catch(() => ({ data: { numbers: [] } })),
        axios.get(`${API_BASE_URL}/imessage/agents`, { headers }).catch(() => ({ data: { agents: [] } })),
      ]);
      setUsers((u.data || []).map((x) => ({ value: x.user_id, label: `${x.first_name} ${x.last_name}` })));
      const groups = [];
      const devs = (dev.data || []).map((d) => ({ value: `device:${d.device_id}`, label: d.device_name || d.device_id }));
      const was = (wa.data?.numbers || []).map((n) => ({ value: `wa:${n.id}`, label: n.phone || n.label || "WhatsApp number" }));
      const ims = (im.data?.agents || []).filter((a) => a.handle).map((a) => ({ value: `im:${a.id}`, label: a.handle }));
      if (devs.length) groups.push({ label: "SMS Devices", options: devs });
      if (was.length) groups.push({ label: "WhatsApp Numbers", options: was });
      if (ims.length) groups.push({ label: "iMessage Handles", options: ims });
      setSenderOptions(groups);
    })();
  }, []);

  // Stats: SMS analytics + channel metrics, re-fetched when filters change.
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const pick = (p) => selectedSenders.filter((s) => s.value.startsWith(p)).map((s) => s.value.slice(p.length));
    const deviceIds = pick("device:");
    const waIds = pick("wa:");
    const imIds = pick("im:");

    (async () => {
      try {
        const q = new URLSearchParams();
        if (selectedUsers.length) q.append("users", selectedUsers.map((u) => u.value).join(","));
        if (deviceIds.length) q.append("devices", deviceIds.join(","));
        const { data } = await axios.get(`${API_BASE_URL}/dashboard/stats?${q.toString()}`, { headers });
        setAnalytics({
          stats: { ...initialStats, ...(data?.stats || {}) },
          simUsage: data?.simUsage || [],
          deviceLeaders: data?.deviceLeaders || [],
          trend: data?.trend || [],
        });
      } catch (e) {
        notify("Failed to load dashboard data. Please refresh.", "error");
      }
      const cq = new URLSearchParams();
      if (waIds.length) cq.append("wa", waIds.join(","));
      if (imIds.length) cq.append("im", imIds.join(","));
      axios.get(`${API_BASE_URL}/services/dashboard?${cq.toString()}`, { headers })
        .then((r) => setCm(r.data || {}))
        .catch(() => {});
    })();
  }, [selectedSenders, selectedUsers]);

  const { stats } = analytics;

  // ── Template dashboard: real data from stats + per-channel metrics (cm) ──
  const num = (x) => Number(x || 0);
  const wa = cm.whatsapp || {};
  const im = cm.imessage || {};
  const msgSent = num(stats.totalSmsSent) + num(wa.sent) + num(im.sent);
  const msgRecv = num(stats.totalSmsReceived) + num(wa.received) + num(im.received);
  const activeSenders = num(stats.totalDevices) + num(wa.numbers) + num(im.macs);
  const leadTotal = num(wa.leads) + num(im.leads);
  const fmt = (x) => num(x).toLocaleString();

  const chanChart = [];
  if (hasService("sms")) chanChart.push({ channel: "SMS", Sent: num(stats.totalSmsSent), Received: num(stats.totalSmsReceived) });
  if (hasService("whatsapp")) chanChart.push({ channel: "WhatsApp", Sent: num(wa.sent), Received: num(wa.received) });
  if (hasService("imessage")) chanChart.push({ channel: "iMessage", Sent: num(im.sent), Received: num(im.received) });

  const hr = new Date().getHours();
  const greet = hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="dashboard-header dash-head">
            <div>
              <h4>{greet} 👋</h4>
              <p className="dashboard-subtitle">Here's how your channels are performing.</p>
            </div>
            <button className="dash-cta" onClick={() => navigate("/services")}>Manage services</button>
          </div>

          {!anyService && (
            <div className="svc-summary-empty">
              <BsBagPlus size={26} />
              <div className="svc-summary-empty-text">
                <strong>No services yet</strong>
                <p>Subscribe to SMS, WhatsApp, or iMessage to get started.</p>
              </div>
              <button className="svc-summary-cta" onClick={() => navigate("/services")}>Browse Services</button>
            </div>
          )}

          {anyService && (
            <>
              {/* Stat cards */}
              <div className="stats-grid">
                <StatCard title="Messages sent" value={fmt(msgSent)} icon={<BsSendFill />} color="blue" subtitle="Last 30 days" />
                <StatCard title="Messages received" value={fmt(msgRecv)} icon={<BsEnvelopeFill />} color="green" subtitle="Last 30 days" />
                <StatCard title="Active senders" value={fmt(activeSenders)} icon={<BsDeviceSsd />} color="indigo" subtitle="Numbers · devices · handles" />
                <StatCard title="Leads captured" value={fmt(leadTotal)} icon={<BsPeopleFill />} color="amber" subtitle="WhatsApp & iMessage" />
              </div>

              {/* Chart + Channels */}
              <div className="row mt-4 g-4">
                <div className="col-lg-8">
                  <div className="chart-card">
                    <div className="chart-header">
                      <h6 className="chart-title">Messages by channel</h6>
                      <span className="text-muted" style={{ fontSize: ".8rem" }}>Last 30 days</span>
                    </div>
                    <div className="chart-wrapper" style={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chanChart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                          <XAxis dataKey="channel" tick={{ fontSize: 12, fill: "#8b8fa3" }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} />
                          <YAxis tick={{ fontSize: 12, fill: "#8b8fa3" }} tickLine={false} axisLine={false} allowDecimals={false} />
                          <Tooltip contentStyle={{ background: "#0d0f16", color: "#eef0f6", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, fontSize: "0.85rem" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                          <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: "0.85rem" }} />
                          <Bar dataKey="Sent" fill="#7c7cff" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Received" fill="#00b9ff" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="chart-card">
                    <h6 className="chart-title">Channels</h6>
                    <div className="dash-chans">
                      {hasService("sms") && (
                        <div className="dash-chan" onClick={() => navigate("/devices")}>
                          <div className="dash-chan-ic" style={{ background: "rgba(99,102,241,.16)", color: "#a5a8ff" }}><BsAndroid2 /></div>
                          <div>
                            <div className="dash-chan-nm">SMS</div>
                            <div className="dash-chan-sub">{num(stats.totalDevices)} device{num(stats.totalDevices) === 1 ? "" : "s"}</div>
                          </div>
                          <span className={`dash-chan-st ${num(stats.activeDevices) > 0 ? "on" : "off"}`}><span className="d" />{num(stats.activeDevices) > 0 ? "Live" : "Offline"}</span>
                        </div>
                      )}
                      {hasService("whatsapp") && (
                        <div className="dash-chan" onClick={() => navigate("/whatsapp")}>
                          <div className="dash-chan-ic" style={{ background: "rgba(37,211,102,.16)", color: "#7de8a8" }}><BsWhatsapp /></div>
                          <div>
                            <div className="dash-chan-nm">WhatsApp</div>
                            <div className="dash-chan-sub">{num(wa.numbers)} number{num(wa.numbers) === 1 ? "" : "s"} · {num(wa.connected)} connected</div>
                          </div>
                          <span className={`dash-chan-st ${num(wa.connected) > 0 ? "on" : "off"}`}><span className="d" />{num(wa.connected) > 0 ? "Live" : "Idle"}</span>
                        </div>
                      )}
                      {hasService("imessage") && (
                        <div className="dash-chan" onClick={() => navigate("/imessage")}>
                          <div className="dash-chan-ic" style={{ background: "rgba(10,132,255,.16)", color: "#8ec4ff" }}><BsChatFill /></div>
                          <div>
                            <div className="dash-chan-nm">iMessage</div>
                            <div className="dash-chan-sub">{num(im.macs)} handle{num(im.macs) === 1 ? "" : "s"}</div>
                          </div>
                          <span className={`dash-chan-st ${num(im.online) > 0 ? "on" : "off"}`}><span className="d" />{num(im.online) > 0 ? "Live" : "Syncing"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent activity */}
              <div className="dash-activity">
                <h6 className="chart-title" style={{ marginBottom: 6 }}>Recent activity</h6>
                {activity.length === 0 ? (
                  <div className="chart-empty"><p>No recent activity yet.</p></div>
                ) : (
                  <div className="table-responsive">
                    <table className="dash-act-table">
                      <thead>
                        <tr><th>Time</th><th>Channel</th><th>Device</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {activity.map((a, i) => {
                          const ch = a.channel === "whatsapp"
                            ? { l: "WhatsApp", c: "wa", ic: <BsWhatsapp /> }
                            : a.channel === "imessage"
                            ? { l: "iMessage", c: "im", ic: <BsChatFill /> }
                            : { l: "SMS", c: "sms", ic: <BsAndroid2 /> };
                          const isIn = a.direction === "in";
                          const okStatus = isIn || a.status === "sent" || a.status === "delivered" || a.status === "done";
                          const errStatus = a.status === "failed" || a.status === "error";
                          return (
                            <tr key={i}>
                              <td className="act-muted">{fmtAgo(a.ts)}</td>
                              <td><span className={`act-pill ${ch.c}`}>{ch.ic} {ch.l} {isIn ? "↓" : "↑"}</span></td>
                              <td>{a.sender || "—"}</td>
                              <td><span className={`act-tag ${okStatus ? "ok" : errStatus ? "err" : "pend"}`}>{isIn ? "received" : a.status || "queued"}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <div className={`stat-card stat-${color}`}>
      <div className="stat-icon-wrap">
        <div className={`stat-icon stat-icon-${color}`}>{icon}</div>
      </div>
      <div className="stat-body">
        <p className="stat-title">{title}</p>
        <p className="stat-value">{value}</p>
        {subtitle && <span className="stat-subtitle">{subtitle}</span>}
      </div>
    </div>
  );
}

function SimUsageCard({ data }) {
  if (!data.length) {
    return (
      <div className="chart-card">
        <h6 className="chart-title">SIM Usage by Device</h6>
        <div className="chart-empty">
          <p>No SMS activity for the selected filters.</p>
        </div>
      </div>
    );
  }

  // Group rows by device — each row has device_name, sim_slot, sent, received
  const deviceMap = {};
  data.forEach((row) => {
    const key = row.device_fingerprint || row.device_name;
    if (!deviceMap[key]) {
      deviceMap[key] = { name: row.device_name || key, SIM1: 0, SIM2: 0, Unknown: 0 };
    }
    const slot = (row.sim_slot || "Unknown").toUpperCase();
    const total = parseInt(row.sent, 10) + parseInt(row.received, 10);
    if (slot === "SIM1") deviceMap[key].SIM1 += total;
    else if (slot === "SIM2") deviceMap[key].SIM2 += total;
    else deviceMap[key].Unknown += total;
  });

  const chartData = Object.values(deviceMap);
  const hasUnknown = chartData.some((d) => d.Unknown > 0);

  return (
    <div className="chart-card">
      <h6 className="chart-title">SIM Usage by Device</h6>
      <div className="chart-wrapper" style={{ height: Math.max(260, chartData.length * 50 + 60) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: "#64748b" }}
              width={80}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                background: "#0d0f16", color: "#eef0f6",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                fontSize: "0.85rem",
              }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              iconSize={10}
              wrapperStyle={{ fontSize: "0.85rem" }}
            />
            <Bar dataKey="SIM1" stackId="a" fill="#7c7cff" radius={[0, 0, 0, 0]} name="SIM 1" />
            <Bar dataKey="SIM2" stackId="a" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="SIM 2" />
            {hasUnknown && (
              <Bar dataKey="Unknown" stackId="a" fill="#94a3b8" radius={[0, 4, 4, 0]} name="Unknown" />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DeviceLeadersCard({ data }) {
  const maxSent = data.length ? Math.max(...data.map((d) => d.sent), 1) : 1;
  const MEDAL_COLORS = ["#facc15", "#94a3b8", "#cd7f32"];

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h6 className="chart-title">
          <BsTrophyFill className="trophy-icon" /> Top Sending Devices
        </h6>
      </div>
      {data.length === 0 ? (
        <div className="chart-empty">
          <p>No outbound traffic for this timeframe.</p>
        </div>
      ) : (
        <div className="leaders-grid">
          {data.map((device, index) => {
            const pct = (device.sent / maxSent) * 100;
            return (
              <div key={device.device_fingerprint} className="leader-row">
                <div className="leader-rank">
                  {index < 3 ? (
                    <span className="leader-medal" style={{ color: MEDAL_COLORS[index] }}>
                      {index === 0 ? "1st" : index === 1 ? "2nd" : "3rd"}
                    </span>
                  ) : (
                    <span className="leader-number">#{index + 1}</span>
                  )}
                </div>
                <div className="leader-info">
                  <div className="leader-name">{device.device_name}</div>
                  <div className="leader-bar-track">
                    <div
                      className="leader-bar-fill"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="leader-count">{device.sent}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrendCard({ data }) {
  if (!data.length) {
    return (
      <div className="chart-card trend-card">
        <h6 className="chart-title">7-Day Volume Trend</h6>
        <div className="chart-empty">
          <p>No SMS traffic logged in the past week.</p>
        </div>
      </div>
    );
  }

  const chartData = data.map((day) => ({
    date: new Date(day.day).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
    Sent: day.sent,
    Received: day.received,
  }));

  return (
    <div className="chart-card trend-card">
      <h6 className="chart-title">7-Day Volume Trend</h6>
      <div className="chart-wrapper" style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.sent} stopOpacity={0.2} />
                <stop offset="95%" stopColor={CHART_COLORS.sent} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradReceived" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.received} stopOpacity={0.2} />
                <stop offset="95%" stopColor={CHART_COLORS.received} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: "#64748b" }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#64748b" }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                background: "#0d0f16", color: "#eef0f6",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                fontSize: "0.85rem",
              }}
            />
            <Area
              type="monotone"
              dataKey="Sent"
              stroke={CHART_COLORS.sent}
              strokeWidth={2.5}
              fill="url(#gradSent)"
              dot={{ r: 4, fill: CHART_COLORS.sent, strokeWidth: 0 }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
            />
            <Area
              type="monotone"
              dataKey="Received"
              stroke={CHART_COLORS.received}
              strokeWidth={2.5}
              fill="url(#gradReceived)"
              dot={{ r: 4, fill: CHART_COLORS.received, strokeWidth: 0 }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={10}
              wrapperStyle={{ fontSize: "0.85rem", paddingBottom: 8 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
